import { useState, useRef, useEffect } from 'react';
import { Scan, Upload, ArrowUp, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { SectionHeader } from './SectionHeader';
import { HistoryEntry } from '@/types/fitness';
import { toast } from 'sonner';
import { toastError } from '@/lib/toastError';
import { supabase } from '@/integrations/supabase/client';

interface EvaluationSectionProps {
  ultimoRegistro: HistoryEntry;
  activeUser?: string;
  onDataExtracted?: () => void;
}

interface ExtractedMetrics {
  peso: number | null;
  grasa: number | null;
  imc: number | null;
  masa_libre_grasa: number | null;
  agua: number | null;
  grasa_visceral: number | null;
  metabolismo_basal: number | null;
  masa_muscular: number | null;
}

export function EvaluationSection({ ultimoRegistro, activeUser = '', onDataExtracted }: EvaluationSectionProps) {
  const [uploading, setUploading] = useState(false);
  const [extracted, setExtracted] = useState<ExtractedMetrics | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset extracted data when switching profiles
  useEffect(() => {
    setExtracted(null);
  }, [activeUser]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setExtracted(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Debes iniciar sesión para subir reportes');
        return;
      }

      // Upload to storage
      const ext = file.name.split('.').pop();
      const filePath = `${user.id}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('inbody-reports')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        toastError('Error al subir el archivo de evaluación', {
          section: 'inbody-upload',
          extra: {
            errorMessage: uploadError.message,
            fileSize: file.size,
            fileType: file.type,
          },
        });
        return;
      }

      toast.info('Archivo subido, analizando con IA...');

      // Call edge function
      const { data, error } = await supabase.functions.invoke('extract-inbody', {
        body: { imagePath: filePath, profileName: activeUser },
      });

      if (error || !data?.success) {
        let serverError = data?.error || 'Error al analizar el reporte';

        if (error && 'context' in error && error.context) {
          try {
            const errorBody = await error.context.json();
            if (errorBody?.error) serverError = errorBody.error;
          } catch {
            // keep fallback message
          }
        }

        console.error('Extraction error - data:', JSON.stringify(data), '| error:', JSON.stringify(error), '| message:', serverError);
        toastError(serverError, {
          section: 'inbody-extract-edge-function',
          extra: {
            edgeError: error,
            edgeData: data,
          },
        });
        return;
      }

      setExtracted(data.metrics);
      if (data.duplicated) {
        toast.info('Este reporte ya estaba registrado. Los datos se mantienen actualizados.');
      } else {
        toast.success('¡Reporte analizado y guardado exitosamente!');
      }
      onDataExtracted?.();
    } catch (err: any) {
      console.error('Unexpected error:', err);
      toastError('Ocurrió un error inesperado al procesar tu reporte', {
        section: 'inbody-flow',
        extra: { errorMessage: err?.message, stack: err?.stack },
      });
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Formato no soportado. Usa JPG, PNG, WEBP o PDF.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo es muy grande (máx 10MB)');
      return;
    }

    handleUpload(file);
    e.target.value = '';
  };

  const metricLabels: { key: keyof ExtractedMetrics; label: string; unit: string }[] = [
    { key: 'peso', label: 'Peso', unit: 'kg' },
    { key: 'grasa', label: 'Grasa Corporal', unit: '%' },
    { key: 'imc', label: 'IMC', unit: '' },
    { key: 'masa_muscular', label: 'Masa Muscular', unit: 'kg' },
    { key: 'masa_libre_grasa', label: 'Masa Libre de Grasa', unit: 'kg' },
    { key: 'agua', label: 'Agua Corporal', unit: 'L' },
    { key: 'grasa_visceral', label: 'Grasa Visceral', unit: '' },
    { key: 'metabolismo_basal', label: 'Metabolismo Basal', unit: 'kcal' },
  ];

  return (
    <section id="evaluación" className="scroll-mt-32">
      <SectionHeader title="Evaluación" subtitle="Bioimpedancia IA" icon={Scan} helpId="evaluacion" />
      
      <div className="bg-primary text-primary-foreground rounded-2xl md:rounded-3xl p-5 md:p-12 overflow-hidden relative shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-accent rounded-full blur-[100px] opacity-20 pointer-events-none"></div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center relative z-10">
          <div className="space-y-6">
            <h3 className="text-2xl md:text-4xl font-black italic uppercase tracking-tighter leading-tight">
              Escanea tu reporte <br/>
              <span className="text-accent">Smart Fit</span>
            </h3>
            <p className="text-muted-foreground text-sm font-medium max-w-sm leading-relaxed">
              Sube una foto de tu ticket de InBody o báscula de bioimpedancia. Nuestra IA extraerá automáticamente tus métricas para actualizar tu gráfica.
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden"
              onChange={onFileChange}
            />

            <button 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-accent text-accent-foreground px-6 md:px-8 py-3 md:py-4 rounded-full font-black uppercase tracking-widest text-xs md:text-sm hover:bg-card hover:text-foreground transition-all flex items-center gap-3 group disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]"
            >
              {uploading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Analizando...
                </>
              ) : (
                <>
                  <Upload size={20} />
                  Subir Reporte
                  <ArrowUp size={16} className="group-hover:-translate-y-1 transition-transform" />
                </>
              )}
            </button>
          </div>
          
          {extracted ? (
            <div className="bg-primary-foreground/10 rounded-2xl p-6 backdrop-blur-md border border-primary-foreground/10">
              <div className="flex items-center gap-4 mb-6">
                <CheckCircle className="text-accent" size={24} />
                <div className="text-xs font-bold uppercase tracking-widest">Datos Extraídos por IA</div>
              </div>
              <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
                {metricLabels.map(({ key, label, unit }) => (
                  <div key={key} className="flex justify-between border-b border-primary-foreground/10 pb-2">
                    <span className="text-sm text-muted-foreground">{label}</span>
                    {extracted[key] != null ? (
                      <span className="font-bold text-accent">
                        {extracted[key]} {unit}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs flex items-center gap-1">
                        <AlertCircle size={12} /> No detectado
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-primary-foreground/10 rounded-2xl p-6 backdrop-blur-md border border-primary-foreground/10">
              <div className="flex items-center gap-4 mb-6">
                <FileText className="text-accent" size={24} />
                <div className="text-xs font-bold uppercase tracking-widest">Última Sincronización</div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between border-b border-primary-foreground/10 pb-2">
                  <span className="text-sm text-muted-foreground">Fecha</span>
                  <span className="font-bold">{ultimoRegistro.fecha}</span>
                </div>
                <div className="flex justify-between border-b border-primary-foreground/10 pb-2">
                  <span className="text-sm text-muted-foreground">Peso</span>
                  <span className="font-bold text-accent">{ultimoRegistro.peso} kg</span>
                </div>
                <div className="flex justify-between border-b border-primary-foreground/10 pb-2">
                  <span className="text-sm text-muted-foreground">Grasa</span>
                  <span className="font-bold text-accent">{ultimoRegistro.grasa} %</span>
                </div>
                <div className="flex justify-between border-b border-primary-foreground/10 pb-2">
                  <span className="text-sm text-muted-foreground">IMC</span>
                  <span className="font-bold text-accent">{ultimoRegistro.imc ?? '--'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Masa Libre de Grasa</span>
                  <span className="font-bold text-accent">{ultimoRegistro.masaLibreGrasa != null ? `${ultimoRegistro.masaLibreGrasa} kg` : '--'}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
